import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";

import config from "../../config/api.config";
import { Technician } from "../../store/documentation-slice";
import { wrapText } from "../../utils/wrapText";

import delexLogo from "./delexLogo.png";
import logoForMonitorList from "./logoForMonitorList.png";

Font.register({
  family: "Roboto",
  fonts: [
    {
      src: require("@fontsource/roboto/files/roboto-latin-400-normal.woff"), // Regular
      fontWeight: "normal",
    },
    {
      src: require("@fontsource/roboto/files/roboto-latin-500-normal.woff"), // Bold
      fontWeight: "medium",
    },
    {
      src: require("@fontsource/roboto/files/roboto-latin-400-italic.woff"), // Italic
      fontStyle: "italic",
      fontWeight: "normal",
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    padding: 48,
    paddingBottom: 90,
    fontFamily: "Roboto",
  },
  title: {
    fontSize: 9,
    flexDirection: "row",
    display: "flex",
    marginBottom: 4,
    color: "#8493AB",
  },
  documentName: {
    color: "#3d5170",
    fontWeight: "medium",
    fontSize: 24,
  },
  text: {
    flexDirection: "column",
    display: "flex",
    color: "#3d5170",
    fontSize: 13.5,
    minWidth: 30,
    fontWeight: "medium",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 15,
    paddingBottom: 28.8,
    paddingTop: 13.875,
  },
  headerTitle: {
    fontSize: 11.25,
  },
  headerLabel: {
    color: "#8493AB",
  },
  headerValue: {
    color: "#3D5170",
  },
  appointmentInfo: {
    flexDirection: "row",
    display: "flex",
    justifyContent: "space-between",
    alignContent: "center",
    marginBottom: 18,
    marginTop: 15,
  },
  wrapper: {
    flexDirection: "column",
    flexGrow: 1,
  },
  table: {
    marginTop: 9.5,
    borderTopWidth: 0.75,
    borderBottomWidth: 0.37,
    borderColor: "#CED3DC",
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 0.37,
    borderLeftWidth: 0.75,
    borderRightWidth: 0.75,
    borderColor: "#CED3DC",
  },
  tableRow: {
    flexDirection: "row",
    borderTopWidth: 0.37,
    borderLeftWidth: 0.75,
    borderRightWidth: 0.75,
    borderBottomWidth: 0.37,
    borderColor: "#CED3DC",
  },
  tableTitle: {
    fontSize: 9,
    padding: 5,
    borderRightWidth: 0.75,
    borderRightColor: "#CED3DC",
    color: "#8493AB",
  },
  tableCell: {
    fontSize: 11.25,
    padding: 5,
    borderRightWidth: 0.75,
    borderRightColor: "#CED3DC",
    color: "#3D5170",
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 20,
    right: 20,
    height: 50,
    flexDirection: "row",
    display: "flex",
    justifyContent: "space-between",
    fontSize: 7,
    color: "#3d5170",
    paddingRight: 20,
    paddingLeft: 20,
  },
  footerTitle: {
    fontSize: 9,
    fontWeight: "medium",
    marginBottom: 4,
  },
  footerItem: {
    flexGrow: 1,
  },
  thirdItem: {
    flex: 1,
    textAlign: "left",
  },
  imageContainer: {
    alignItems: "flex-end",
  },
  image: {
    width: 108,
    height: 81,
    objectFit: "contain",
  },
});

const monitorStatusLabels: Record<string, string> = {
  maintenancePerformed: "Pflege",
  noInfestation: "kein Befall",
  infestation: "Befall",
  unreachableOrMissing: "Nicht erreichbar",
  notChecked: "Nicht kontrolliert",
  adjusted: "Anpassung",
  removed: "Anpassung",
};

const formatDate = (date: string) => {
  return new Intl.DateTimeFormat("de-DE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(date));
};

const columnWidths = {
  area: 130,
  monitorType: 130,
  number: 45,
  location: 75,
  status: 70,
  materials: 240,
  comment: 245,
};

type DocumentationProps = {
  appointment: any;
  propertyAreaMonitors: any[];
};

const Footer = () => (
  <View style={styles.footer} fixed>
    <View style={styles.footerItem}>
      <Text style={styles.footerTitle}>{config.companyName}</Text>
      <Text style={styles.title}>{config.companyAddress}</Text>
      <Text style={styles.title}>{config.companyCity}</Text>
    </View>

    <View style={[styles.footerItem, styles.thirdItem]}>
      <Text style={styles.title}>Seite</Text>
      <Text
        style={styles.footerTitle}
        render={({ pageNumber, totalPages }) =>
          `${pageNumber} von ${totalPages}`
        }
        fixed
      />
    </View>
  </View>
);

export const MonitorListPDF: React.FC<DocumentationProps> = ({
  appointment,
  propertyAreaMonitors,
}) => {
  const logoUrl =
    config.companyName === "F+M Schädlingsbekämpfungs GmbH"
      ? logoForMonitorList
      : delexLogo;

  const appointmentType =
    appointment.typeOfAppointment === "auftrag"
      ? "Bekämpfung"
      : appointment.typeOfAppointment === "other"
        ? "Sonstiges"
        : appointment.typeOfAppointment;

  return (
    <Document>
      <Page size={[1052.25, 744]} style={styles.page}>
        <View style={styles.wrapper}>
          <View style={styles.header}>
            <View>
              <Text style={styles.headerTitle}>
                <Text style={styles.headerLabel}>Kunde: </Text>
                <Text style={styles.headerValue}>
                  {appointment.additionalData.customer.name}
                </Text>
              </Text>

              <Text style={styles.headerTitle}>
                <Text style={styles.headerLabel}>Standort: </Text>
                <Text style={styles.headerValue}>
                  {appointment.additionalData.property.name}{" "}
                  {appointment.additionalData.property.address}
                </Text>
              </Text>
            </View>
            <View style={styles.imageContainer}>
              <Image src={logoUrl} style={styles.image} />
            </View>
          </View>
          <Text style={styles.documentName}>Monitorliste</Text>
          <View style={styles.appointmentInfo}>
            <View>
              <Text style={styles.title}>Leistung</Text>
              {appointment.typeOfAppointment ? (
                <Text style={styles.text}>
                  {appointmentType.charAt(0).toUpperCase() +
                    appointmentType.slice(1)}
                </Text>
              ) : null}
            </View>

            <View>
              <Text style={styles.title}>Datum</Text>
              <Text style={styles.text}>{formatDate(appointment.date)}</Text>
            </View>

            <View style={{ paddingRight: 79 }}>
              <Text style={styles.title}>Verantwortliche Techniker</Text>
              {appointment.technicians.length > 0
                ? appointment.technicians.map(
                    (technician: Technician, index: number) => (
                      <View key={index}>
                        <Text style={styles.text}>
                          {technician.firstName} {technician.lastName}
                        </Text>
                      </View>
                    )
                  )
                : null}
            </View>
          </View>
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableTitle, { width: columnWidths.area }]}>
                Ebene
              </Text>

              <Text
                style={[styles.tableTitle, { width: columnWidths.monitorType }]}
              >
                Monitortyp
              </Text>

              <Text style={[styles.tableTitle, { width: columnWidths.number }]}>
                Nr.
              </Text>

              <Text
                style={[styles.tableTitle, { width: columnWidths.location }]}
              >
                Standort
              </Text>

              <Text style={[styles.tableTitle, { width: columnWidths.status }]}>
                Status
              </Text>

              <Text
                style={[styles.tableTitle, { width: columnWidths.materials }]}
              >
                Materialeinsatz
              </Text>

              <Text
                style={[
                  styles.tableTitle,
                  {
                    width: columnWidths.comment,
                    borderRightWidth: 0,
                  },
                ]}
              >
                Bemerkung
              </Text>
            </View>
            {propertyAreaMonitors.map((area: any) =>
              area.monitors.map((monitor: any) => (
                <View
                  key={monitor.monitorId}
                  style={styles.tableRow}
                  wrap={false}
                >
                  <Text
                    style={[styles.tableCell, { width: columnWidths.area }]}
                  >
                    {wrapText(area.label)}
                  </Text>

                  <Text
                    style={[
                      styles.tableCell,
                      { width: columnWidths.monitorType },
                    ]}
                  >
                    {monitor.monitorType || "-"}
                  </Text>

                  <Text
                    style={[styles.tableCell, { width: columnWidths.number }]}
                  >
                    {monitor.number || "-"}
                  </Text>

                  <Text
                    style={[styles.tableCell, { width: columnWidths.location }]}
                  >
                    {monitor.location || "-"}
                  </Text>

                  <Text
                    style={[styles.tableCell, { width: columnWidths.status }]}
                  >
                    {monitorStatusLabels[monitor.status] ??
                      monitor.status ??
                      "-"}
                  </Text>

                  <Text
                    style={[
                      styles.tableCell,
                      { width: columnWidths.materials },
                    ]}
                  >
                    {monitor.materials?.length
                      ? monitor.materials.map(
                          (material: any, index: number) => (
                            <Text key={index}>
                              <Text style={{ fontWeight: "medium" }}>
                                {material.quantity}x
                              </Text>{" "}
                              {material.name}
                              {index < monitor.materials.length - 1 ? "\n" : ""}
                            </Text>
                          )
                        )
                      : "-"}
                  </Text>

                  <Text
                    style={[
                      styles.tableCell,
                      {
                        width: columnWidths.comment,
                        borderRightWidth: 0,
                        fontStyle: "italic",
                      },
                    ]}
                  >
                    {monitor.comment || "-"}
                  </Text>
                </View>
              ))
            )}
          </View>
        </View>
        <Footer />
      </Page>
    </Document>
  );
};
